import { useCallback, useMemo } from 'react';

import { activeEntryOf } from './state/loggerDraft';
import type { LoggerDraft } from './state/loggerDraftTypes';
import { groupMembersOf } from './supersetNotebook';
import { useCoachNotes } from './useCoachNotes';
import { useTemplateName } from './useTemplateName';
import { usePlanCues, cueForEntry } from './usePlanCues';
import type { LoggerHostDeps } from './hostDeps';

// What LoggerScreen's header and tip read: the plan note for the "?" tip, the
// coach notes, the row in focus and the manual rest. Moved out of LoggerScreen
// for its size, unchanged.
export function useLoggerHeaderState({
  draft,
  apiFetch,
  tipEntryIdx,
  activeId,
  startManualRest,
  clearRestInvite,
}: {
  draft: LoggerDraft;
  apiFetch: LoggerHostDeps['apiFetch'];
  tipEntryIdx: number | null;
  activeId: string | null;
  startManualRest: (exerciseId?: string) => void;
  clearRestInvite: () => void;
}) {
  // Per-exercise plan instruction for the "?" tip, resolved per-slug from the
  // plan (same source as the FocusPanel cue), since it's no longer carried on
  // the draft. The tip entry is always the active entry.
  const planCues = usePlanCues(draft.fromPlanId ?? draft.coachNotesRef, apiFetch);
  // LG-34: resolved by (slug, rank) so a day with the same exercise in two
  // slots shows each slot's own instruction. The memo guards the re-renders
  // that are NOT a dispatch: every action rebuilds `draft.entries`, so on the
  // logging path this recomputes anyway: O(entries), cheap enough to leave.
  const tipPlanNote = useMemo(
    () =>
      tipEntryIdx !== null
        ? cueForEntry(planCues, draft.entries, draft.entries[tipEntryIdx])
            ?.instruction
        : undefined,
    [planCues, draft.entries, tipEntryIdx],
  );
  // Coach notes for the header "?": captured at Start (draft.coachNotes,
  // offline-safe), with a render-time fallback that re-resolves from the
  // program/plan so resumed / pre-3.14 / edited / repeated sessions also
  // surface the "?" (FK + resolve at render, no snapshot). The reference is
  // `fromPlanId` for a live plan-started draft, or `coachNotesRef` for a
  // past session re-opened in Edit/Repeat (hydrateFromWorkout copies it from
  // the saved Workout.fromPlanId; display-only, never the save id).
  const coachNotes = useCoachNotes(
    draft.fromPlanId ?? draft.coachNotesRef,
    draft.coachNotes,
    apiFetch,
  );
  // #318: the notes panel title.
  const templateName = useTemplateName(
    draft.fromPlanId ?? draft.coachNotesRef,
    apiFetch,
  );
  // The row the header speaks for: it asks two questions of it (superset? which
  // exercise?) and both must read the SAME cursor resolution: the header
  // renders above LoggerContext, so it cannot borrow the subtree's answer, and
  // two hand-rolled `find`s over one cursor are what drift. ⚠️ The subtree
  // still rolls its own (useTimelineLogger.ts, resolvedActiveId), because it
  // needs the INDEX too (LG-79): registered in consolidation.md, not silently
  // left.
  const activeEntry = activeEntryOf(draft.entries, activeId);
  // The header clock lights up inside a superset (MX-5 D9): there the automatic
  // rest is the 4s implement change, so "start a real rest" is the one moment
  // that affordance answers a live question.
  const inSuperset =
    groupMembersOf(draft.entries, activeEntry?.group?.id).length >= 2;
  // TM-19: the manual rest is BOUND to the exercise in focus. It used to arm
  // unbound on purpose (a header affordance owns no row), and the price was
  // that restarting a rest from the header lost the ⚙: FocusPanelRest draws it
  // only for a tagged rest, and the picker no-ops on a null slug. With nothing
  // in focus (Option B) there is still no exercise to bind, and the banner is
  // gearless exactly as before.
  // A rest started by hand follows no set, so it carries no offer.
  const onStartRest = useCallback(() => {
    clearRestInvite();
    startManualRest(activeEntry?.exerciseId);
  }, [clearRestInvite, startManualRest, activeEntry?.exerciseId]);

  return {
    tipPlanNote,
    coachNotes,
    templateName,
    activeEntry,
    inSuperset,
    onStartRest,
  };
}
